/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component, createRef } from 'react';
import { object, func, bool, shape, string } from 'prop-types';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';

import p from 'polyglot/polyglot';

import { setInviteId as setInviteIdAction } from 'actions/signup';

import { authenticate as authenticateAction } from 'actions/auth';

import {
  BOOTSTRAP_SUCCESS,
  bootstrap as bootstrapAction,
  refreshLocale as refreshLocaleAction,
  setReady as setReadyAction,
} from 'actions/app';

import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import SuccessMessage from 'components/SuccessMessage/SuccessMessage';

import ProtectedRoute from 'components/ProtectedRoute/ProtectedRoute';

import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import PageLoader from 'components/PageLoader/PageLoader';

import SignupModal from 'components/SignupModal/SignupModal';
import LoginModal from 'components/LoginModal/LoginModal';
import ShortlistBasket from 'components/ShortlistBasket/ShortlistBasket';

import Home from 'components/Home/Home';

import StaticPage from 'components/StaticPage/StaticPage';
import TextPage from 'components/TextPage/TextPage';

import ResetPassword from 'components/ResetPassword/ResetPassword';
import UpdatePassword from 'components/UpdatePassword/UpdatePassword';

import Signup from 'components/Signup/Signup';

import Account from 'components/Account/Account';
import { ViewProfile } from 'components/Profile/Profile';
import { ViewCompany } from 'components/Company/Company';
import ShortlistCreate from 'components/ShortlistCreate/ShortlistCreate';
import LikesList from 'components/LikesList/LikesList';
import Invite from 'components/Invite/Invite';
import Talents from 'components/Talents/Talents';
import SendRequests from 'components/SendRequests/SendRequests';
import Agency from 'components/Agency/Agency';
import Talent from 'components/Talent/Talent';

import Blog from 'components/Blog/Blog';
import BlogPost from 'components/BlogPost/BlogPost';

import MetaTags from 'components/MetaTags/MetaTags';

import styles from './app.scss';

/**
 * This is the App component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class App extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    location: object.isRequired,
    app: shape({
      loading: bool.isRequired,
      ready: bool.isRequired,
      locale: string.isRequired,
    }).isRequired,
    auth: object.isRequired,
    authenticate: func.isRequired,
    bootstrap: func.isRequired,
    refreshLocale: func.isRequired,
    setReady: func.isRequired,
    setInviteId: func.isRequired,
  };

  constructor() {
    super();
    this.shortlistButton = createRef();
  }

  componentDidMount() {
    const {
      app: { locale },
      auth: { isAuthenticated, provider, asUser },
      authenticate,
      setInviteId,
    } = this.props;

    const query = queryString.parse(location.search);
    const inviteId = query && query.invite ? query.invite : null;

    if (inviteId) {
      setInviteId(inviteId);
    }

    if (isAuthenticated) {
      const payload = {
        ...provider,
        language: locale,
      };

      // If asUser is defined in the auth state - include it in the request
      if (asUser) {
        payload.as_user = asUser;
      }

      authenticate(payload).then(() => this.loadBootstrap());
    } else {
      this.loadBootstrap();
    }
  }

  componentDidUpdate(prevProps) {
    const { location } = prevProps;
    const { location: newLocation } = this.props;

    if (location !== newLocation) {
      process.nextTick(() => window.scrollTo(0, 0));
    }
  }

  loadBootstrap = () => {
    const {
      app: { locale },
      auth: { token },
      refreshLocale,
      bootstrap,
      setReady,
    } = this.props;

    bootstrap({ token, locale }).then(({ type, payload }) => {
      if (type === BOOTSTRAP_SUCCESS) {
        p.locale(locale);
        p.extend(payload.translations);

        refreshLocale();
        this.forceUpdate();

        setReady();
      }
    });
  };

  /**
   * Render App.
   *
   * @return {Object}
   */
  render() {
    const {
      app: { ready },
      auth,
    } = this.props;

    if (!ready) {
      return (
        <div className={styles.wrapper}>
          <PageLoader />
          <ErrorMessage />
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <MetaTags page="default" />

        <Header shortlistRef={this.shortlistButton} />
        <ErrorMessage />
        <SuccessMessage />

        <LoginModal />
        <SignupModal />

        <ShortlistBasket parentElem={this.shortlistButton} />

        <div className={styles.content}>
          <Switch>
            <Route exact path="/" component={Home} />

            {/* Login goes to home also, to make sure for old links. */}
            <Route exact path="/login" component={Home} />

            <Route
              exact
              path="/login/password-reset"
              component={ResetPassword}
            />
            <Route strict path="/reset/:resetCode" component={UpdatePassword} />

            <Route exact path="/signup/:signupType?" component={Signup} />
            <Route strict path="/page/:slug/:section?" component={StaticPage} />
            <Route path="/invite" component={Invite} />

            <Route exact path="/news" component={Blog} />
            <Route strict path="/news/category/:category" component={Blog} />
            <Route strict path="/news/:slug" component={BlogPost} />

            <Route path="/agency/models" component={Agency} />
            <Route path="/talent/models" component={Talent} />

            <Route path="/shortlist/create" component={ShortlistCreate} />

            <Route exact path="/talents/:shortlistId?" component={Talents} />
            <Route exact path="/likes/:shortlistId" component={LikesList} />

            <ProtectedRoute path="/account" component={Account} />

            <Redirect exact from="/inbox" to="/account/inbox" />
            <Redirect
              strict
              from="/inbox/:discussionId"
              to="/account/inbox/:discussionId"
            />

            <Route strict path="/profile/:slug" component={ViewProfile} />
            <ProtectedRoute
              strict
              path="/company/:slug"
              component={ViewCompany}
            />

            <ProtectedRoute
              strict
              path="/requests/submit/"
              component={SendRequests}
            />

            <Redirect from="/jobs" to="/account/jobs" />

            <Route path="/:slug" component={TextPage} />
          </Switch>
        </div>

        <Footer isAuth={auth.isAuthenticated} />
      </div>
    );
  }
}

export default withRouter(
  connect(({ app, auth }) => ({ app, auth }), {
    bootstrap: bootstrapAction,
    authenticate: authenticateAction,
    refreshLocale: refreshLocaleAction,
    setReady: setReadyAction,
    setInviteId: setInviteIdAction,
  })(App)
);
